import type { CityResponse } from '@/types'

interface UseCitiesOptions {
  countySlug?: string
  limit?: number
}

interface CitiesApiResponse {
  success: boolean
  data: CityResponse[]
}

export function useCities(options: Ref<UseCitiesOptions> | UseCitiesOptions = {}) {
  const opts = isRef(options) ? options : ref(options)

  const key = computed(() => `cities-${opts.value.countySlug || 'all'}-${opts.value.limit || 'all'}`)

  return useAsyncData<CitiesApiResponse>(
    key,
    () =>
      $fetch('/api/cities', {
        query: {
          countySlug: opts.value.countySlug,
          limit: opts.value.limit,
        },
      }),
    {
      watch: [opts],
    }
  )
}

export function useCity(
  countySlug: string | Ref<string>,
  citySlug: string | Ref<string>
) {
  const countySlugRef = isRef(countySlug) ? countySlug : ref(countySlug)
  const citySlugRef = isRef(citySlug) ? citySlug : ref(citySlug)

  const { data: citiesData, pending, error, refresh, execute } = useCities(
    computed(() => ({ countySlug: countySlugRef.value }))
  )

  const city = computed<CityResponse | null>(() => {
    if (!citiesData.value?.data) return null
    return citiesData.value.data.find((c) => c.slug === citySlugRef.value) ?? null
  })

  return {
    data: city,
    pending,
    error,
    refresh,
    execute,
  }
}
